import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { listRmas } from '@/lib/api/inventory-rmas.js';
import { listQuarantineSummary } from '@/lib/api/warranty.js';
import { formatCurrency, formatDate, prettyLabel } from '@/lib/format.js';
import { EmptyState } from '@/components/common/EmptyState.js';
import { Pagination } from '@/components/common/Pagination.js';

const PAGE_SIZE = 20;

export function RmaListPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [status]);

  const query = useQuery({
    queryKey: ['inventory-rmas', status, page],
    queryFn: () =>
      listRmas({
        ...(status ? { status } : {}),
        page,
        pageSize: PAGE_SIZE,
      }),
  });
  const summaryQuery = useQuery({
    queryKey: ['quarantine-summary'],
    queryFn: listQuarantineSummary,
  });

  const meta = query.data?.meta;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-950">Supplier RMAs</h1>
          <p className="text-sm text-slate-500">Return defective stock to suppliers</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/inventory/rmas/new')}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white"
        >
          <Plus className="h-4 w-4" />
          New RMA
        </button>
      </div>

      {(summaryQuery.data ?? []).length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {(summaryQuery.data ?? []).map((row) => (
            <div key={row.supplierId ?? 'none'} className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
              <div className="text-sm font-semibold text-amber-950">{row.supplierName}</div>
              <div className="mt-1 text-sm text-amber-900">
                {row.count} in quarantine · {formatCurrency(row.totalCost)}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <select
        className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm"
        value={status}
        onChange={(e) => setStatus(e.target.value)}
      >
        <option value="">All statuses</option>
        <option value="preparing">Preparing</option>
        <option value="ready_to_ship">Ready to ship</option>
        <option value="sent">Sent</option>
        <option value="awaiting_supplier">Awaiting supplier</option>
        <option value="credited">Credited</option>
        <option value="replaced">Replaced</option>
        <option value="cancelled">Cancelled</option>
      </select>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
            <tr>
              <th className="px-5 py-3">RMA #</th>
              <th className="px-5 py-3">Supplier</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(query.data?.data ?? []).map((rma) => (
              <tr key={rma.id} className="hover:bg-slate-50">
                <td className="px-5 py-3">
                  <Link className="font-semibold text-primary" to={`/inventory/rmas/${rma.id}`}>
                    {rma.rmaNumber}
                  </Link>
                </td>
                <td className="px-5 py-3">{rma.supplierName ?? rma.supplierId}</td>
                <td className="px-5 py-3">{prettyLabel(rma.status)}</td>
                <td className="px-5 py-3">{formatDate(rma.createdAt)}</td>
              </tr>
            ))}
            {!query.isLoading && (query.data?.data ?? []).length === 0 ? (
              <tr>
                <td colSpan={4}>
                  <EmptyState
                    icon="📦"
                    title="No RMAs"
                    description="Create an RMA to return stock to a supplier."
                    action={{ label: 'New RMA', onClick: () => navigate('/inventory/rmas/new') }}
                  />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        pageSize={meta?.pageSize ?? PAGE_SIZE}
        total={meta?.total ?? 0}
        onPageChange={setPage}
      />
    </div>
  );
}
